import { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Search, Heart, ShoppingBag, User, Menu, Sprout, X } from "lucide-react";
import { Sheet, SheetContent, SheetTitle } from "../ui/sheet";
import { useShop } from "../../context/ShopContext";
import { useAuth } from "../../context/AuthContext";

export const Logo = ({ light }) => (
  <Link to="/" data-testid="brand-logo" className={`flex items-center gap-2 ${light ? "text-[#FBF8F3]" : "text-[#1B3B2B]"}`}>
    <Sprout className="w-5 h-5 text-[#6B8E78]" strokeWidth={1.6} />
    <span className="font-serif text-2xl font-semibold tracking-[0.14em]">SEED</span>
    <span className="hidden sm:inline text-[10px] tracking-[0.3em] uppercase mt-1 opacity-70">Cosmetics</span>
  </Link>
);

function useNav() {
  const { categories } = useShop();
  const cats = categories.filter((c) => c.show_in_nav !== false && !c.parent_id);
  return [
    { to: "/", label: "Home" },
    { to: "/shop", label: "Shop All" },
    ...cats.map((c) => ({ to: `/shop/${c.slug}`, label: c.name })),
    { to: "/shop?tag=new_arrival", label: "New Arrivals" },
    { to: "/shop?tag=bestseller", label: "Best Sellers" },
    { to: "/shop?offers=1", label: "Offers" },
    { to: "/page/about-us", label: "About Us" },
    { to: "/contact", label: "Contact Us" },
  ];
}

export default function Header() {
  const { settings, cartCount, setCartOpen, wishlist } = useShop();
  const { user } = useAuth();
  const [menu, setMenu] = useState(false);
  const [search, setSearch] = useState(false);
  const [q, setQ] = useState("");
  const nav = useNav();
  const navigate = useNavigate();
  const ann = settings.announcement;
  const submit = (e) => {
    e.preventDefault();
    navigate(`/shop?q=${encodeURIComponent(q)}`);
    setSearch(false);
    setMenu(false);
  };
  const iconBtn = "p-2 rounded-full text-[#1B3B2B] hover:bg-[#E2EBE2] transition-colors relative";

  return (
    <>
      {ann?.enabled && ann.text && (
        <div data-testid="announcement-bar" className="bg-[#1B3B2B] text-[#FBF8F3] text-xs sm:text-[13px] text-center py-2 px-4 tracking-wide">
          {ann.link ? <Link to={ann.link} className="hover:underline">{ann.text}</Link> : ann.text}
        </div>
      )}
      <header className="sticky top-0 z-40 backdrop-blur-md bg-[#FFFDF9]/85 border-b border-[#E8E0D5]">
        <div className="container-x flex items-center justify-between h-16 gap-3">
          <div className="flex items-center gap-2">
            <button className={`${iconBtn} lg:hidden`} onClick={() => setMenu(true)} data-testid="mobile-menu-button" aria-label="Open menu">
              <Menu className="w-5 h-5" />
            </button>
            <Logo />
          </div>
          <nav className="hidden lg:flex items-center gap-5 xl:gap-7 text-[13px] tracking-wide">
            {nav.map((n) => (
              <NavLink key={n.to} to={n.to} end data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) => `whitespace-nowrap transition-colors hover:text-[#6B8E78] ${isActive ? "text-[#1B3B2B] font-medium" : "text-stone-600"}`}>
                {n.label}
              </NavLink>
            ))}
          </nav>
          <div className="flex items-center gap-0.5 sm:gap-1">
            <button className={iconBtn} onClick={() => setSearch((s) => !s)} data-testid="header-search-button" aria-label="Search"><Search className="w-5 h-5" /></button>
            <Link to={user ? "/account" : "/login"} className={iconBtn} data-testid="header-account-link" aria-label="Account"><User className="w-5 h-5" /></Link>
            <Link to="/account?tab=wishlist" className={`${iconBtn} hidden sm:inline-flex`} data-testid="header-wishlist-link" aria-label="Wishlist">
              <Heart className="w-5 h-5" />
              {wishlist.length > 0 && <span className="absolute -top-0.5 -right-0.5 text-[10px] bg-[#D48B68] text-white rounded-full w-4 h-4 grid place-items-center">{wishlist.length}</span>}
            </Link>
            <button className={iconBtn} onClick={() => setCartOpen(true)} data-testid="header-cart-button" aria-label="Cart">
              <ShoppingBag className="w-5 h-5" />
              {cartCount > 0 && <span data-testid="cart-count" className="absolute -top-0.5 -right-0.5 text-[10px] bg-[#1B3B2B] text-white rounded-full w-4 h-4 grid place-items-center">{cartCount}</span>}
            </button>
          </div>
        </div>
        {search && (
          <form onSubmit={submit} className="container-x pb-3 fade-up">
            <div className="flex items-center gap-2 bg-white border border-[#E8E0D5] rounded-full px-4 py-2">
              <Search className="w-4 h-4 text-stone-400" />
              <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} data-testid="header-search-input"
                placeholder="Search by product, ingredient or concern…" className="flex-1 bg-transparent outline-none text-sm" />
              <button type="button" onClick={() => setSearch(false)} aria-label="Close search"><X className="w-4 h-4 text-stone-400" /></button>
            </div>
          </form>
        )}
      </header>
      <Sheet open={menu} onOpenChange={setMenu}>
        <SheetContent side="left" className="bg-[#FBF8F3] w-[85vw] max-w-sm">
          <SheetTitle className="sr-only">Menu</SheetTitle>
          <Logo />
          <form onSubmit={submit} className="mt-6 flex items-center gap-2 border border-[#E8E0D5] rounded-full px-3 py-2 bg-white">
            <Search className="w-4 h-4 text-stone-400" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search products" className="flex-1 bg-transparent outline-none text-sm" data-testid="mobile-search-input" />
          </form>
          <nav className="mt-6 flex flex-col">
            {nav.map((n) => (
              <Link key={n.to} to={n.to} onClick={() => setMenu(false)} className="py-3 border-b border-[#E8E0D5] font-serif text-xl text-[#1B3B2B]" data-testid={`mobile-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}>{n.label}</Link>
            ))}
            <Link to="/track-order" onClick={() => setMenu(false)} className="py-3 text-sm text-stone-600">Track Order</Link>
          </nav>
        </SheetContent>
      </Sheet>
    </>
  );
}
